<script lang="ts">
  import { type ButtonProps } from '@athena-lib/Button';
  import clsx from 'clsx';
  import { Type } from '@sinclair/typebox';
  import { MutationButton } from '@athena-lib/MutationButton';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { createCurrencyFormatter } from '@athena-lib/Checkout/utils';
  import { interpolate } from '@athena-lib/Trans/Trans.svelte';
  import { getShoppingCart } from '$lib/shopping-cart.svelte';
  import logger from '$lib/logger';
  import { getText, get } from '$lib/http';
  import { trackAddToCart } from '$lib/gtm/event-trigger';
  import { getCheckoutSdk } from '$lib/checkout.svelte';
  import { checkoutDrawerActions } from '$lib/utilities/checkoutDrawerContext.svelte';

  type AddToCartButtonProps = {
    productId: string | undefined;
    buttonText: string | undefined;
    productType: 'sticker-pack' | 'giftcard' | 'sticker-sample' | undefined;
    productImageUrl?: string;
  };

  const ProductItemSchema = Type.Object({
    item: Type.Object({}),
    exists_in_cart: Type.Optional(Type.Boolean()),
    gtm_item: Type.Object({
      item_category: Type.Union([Type.Literal('custom'), Type.Literal('library')]),
      item_category2: Type.String(),
      item_category3: Type.String(),
      item_category4: Type.Union([Type.String(), Type.Null()]),
      item_category5: Type.Union([Type.String(), Type.Null()]),
      item_category6: Type.Union([Type.String(), Type.Null()]),
      item_id: Type.String(),
      item_name: Type.String(),
      item_variant: Type.String(),
      quantity: Type.Number(),
    }),
    currency: Type.String(),
    price: Type.Object({
      total: Type.Number(),
      totalFormatted: Type.String(),
      totalTaxAmount: Type.Number(),
      totalTaxAmountFormatted: Type.String(),
      unitPrice: Type.Number(),
      unitPriceFormatted: Type.String(),
    }),
  });

  const {
    productId,
    buttonText,
    productType,
    productImageUrl,
    color,
    class: className = '',
    children: _children,
    ...rest
  }: ButtonProps & AddToCartButtonProps = $props();

  if (!productId || !buttonText || !productType) {
    logger().error('productId, buttonText and producType type are required when adding to cart');
  }

  const checkoutSdk = getCheckoutSdk();
  const translations = getCommonTranslations();

  const addProductToCartLegacy = async (productId: string) => {
    const url = `/products/add_to_cart?product_family=promo&product_name=${productType}&product_id=${productId}`;
    try {
      await getText(url, { redirect: 'manual' });
      const {
        exists_in_cart,
        gtm_item: {
          item_category,
          item_category2,
          item_category3,
          item_category4,
          item_category5,
          item_category6,
          item_id,
          item_name,
          item_variant,
          quantity,
        },
        currency,
        price,
      } = await get(`/products/get_product/${productType}?id=${productId}`, ProductItemSchema);
      if (!exists_in_cart) {
        trackAddToCart({
          index: 1,
          price: price.total,
          currency: currency,
          quantity,
          item_category,
          item_category2,
          item_category3,
          item_category4: item_category4 ?? '',
          item_category5: item_category5 ?? '',
          item_category6: item_category6 ?? '',
          item_id,
          item_name,
          item_variant,
        });
      }
      await getShoppingCart().refreshCart();
    } catch (error) {
      logger().error('Error adding product to cart', error);
    }
  };

  const parseProductId = (productId: string): number | undefined => {
    const parsed = Number(productId);

    if (isNaN(parsed)) {
      return undefined;
    }

    return parsed;
  };

  const addGiftCardToCart = async (productId: string) => {
    if (!checkoutSdk) {
      logger().info('Attempted to add a gift-card but sdk is not instantiated');
      return;
    }

    const formatCurrency = createCurrencyFormatter(checkoutSdk.state);

    try {
      const { price } = await get(
        `/products/get_product/giftcard?id=${encodeURIComponent(productId)}`,
        ProductItemSchema,
      );

      const normalizedPrice = price.unitPrice * 100;
      const giftCardName = interpolate(translations.giftCard, {
        amount: formatCurrency(normalizedPrice),
      });

      await checkoutSdk.actions.addGiftCardLineItem(
        giftCardName,
        productImageUrl ?? '',
        productId,
        1,
      );

      checkoutDrawerActions.addItemToCart({
        image: { src: productImageUrl ?? '', alt: giftCardName },
        name: giftCardName,
        formattedSize: '',
      });
    } catch (err) {
      logger().error('Failed to add giftcard to cart', (err as Error).message);
    }
  };

  const addStickerPackToCart = async (productId: string) => {
    if (!checkoutSdk) {
      logger().info('Attempted to add a sticker-pack but sdk is not instantiated');
      return;
    }

    try {
      const id = parseProductId(productId);

      if (!id) {
        throw new Error('Invalid productId');
      }

      await checkoutSdk.actions.addStickerPackLineItem(id, 1);
    } catch (err) {
      logger().error('Failed to add sticker-pack to cart', (err as Error).message);
    }
  };

  const addProductToCart = (productType: string, productId: string) => {
    if (!checkoutSdk) {
      return addProductToCartLegacy(productId);
    }

    if (productType === 'giftcard') {
      return addGiftCardToCart(productId);
    }

    if (productType === 'sticker-pack') {
      return addStickerPackToCart(productId);
    }

    return Promise.resolve(undefined);
  };
</script>

<!-- //NOTE: we don't always need product_id with for instane sample packs -->

<MutationButton
  {color}
  {...rest}
  class={clsx('relative flex w-auto items-center justify-center', className)}
  element="button"
  createPromise={() => addProductToCart(productType ?? '', productId ?? '')}
>
  {buttonText}
</MutationButton>
